/**
 * @voltxampere/web — Preset card mini-canvas SVG thumbnails
 * (Sprint 18 Paket B B5).
 *
 * Mockup zenit-platform.html SEKME 3'teki .preset-art SVG
 * sembolleri tam karşılığı. Her preset için 160×120 viewBox bir
 * Lit template — pane-presets card'ında lazy render edilir.
 *
 * Visual dil:
 *   - 160×120 viewBox, %100/%100 width/height
 *   - Birincil stroke: var(--ivory) opacity 0.8, 1px
 *   - Aksent stroke: var(--brass-hot), ölçüm noktaları + akım
 *   - Toprak hattı: 3 azalan paralel çizgi (mockup pattern)
 *   - Dotted line: stroke-dasharray="2,3" — wire/beklenmedik
 *
 * Yeni preset eklendiğinde bu dosyaya thumbnail ekle. Kayıt yoksa
 * thumbnailFor() default fallback (custom-thumbnail) döner —
 * görsel tutarlılık korunur ama Şefim eksiği fark eder.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { html, svg, type TemplateResult } from "lit";

const STROKE = "var(--ivory)";
const ACCENT = "var(--brass-hot)";

function frame(...children: TemplateResult[]): TemplateResult {
  return html`<svg viewBox="0 0 160 120" width="100%" height="100%" aria-hidden="true">
    ${children}
  </svg>`;
}

function ground(x: number, y: number): TemplateResult {
  return svg`
    <line x1=${x - 12} y1=${y} x2=${x + 12} y2=${y} stroke=${STROKE} stroke-width="1.4" opacity="0.8" />
    <line x1=${x - 8} y1=${y + 5} x2=${x + 8} y2=${y + 5} stroke=${STROKE} stroke-width="1.2" opacity="0.7" />
    <line x1=${x - 4} y1=${y + 10} x2=${x + 4} y2=${y + 10} stroke=${STROKE} stroke-width="1" opacity="0.6" />
  `;
}

function nodeDot(x: number, y: number, label?: string): TemplateResult {
  return svg`
    <circle cx=${x} cy=${y} r="3" fill=${ACCENT} />
    ${label ? svg`<text x=${x + 6} y=${y + 4} font-family="EB Garamond" font-style="italic" font-size="10" fill=${ACCENT}>${label}</text>` : svg``}
  `;
}

const VOLTAGE_DIVIDER = frame(svg`
  <!-- top rail -->
  <line x1="30" y1="20" x2="130" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- V1 source -->
  <circle cx="30" cy="55" r="11" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="20" x2="30" y2="44" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="66" x2="30" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- R1 zigzag -->
  <path d="M 130 20 L 130 28 L 125 32 L 135 38 L 125 44 L 135 50 L 125 56 L 130 60" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(130, 70, "n₂")}
  <!-- R2 zigzag -->
  <path d="M 130 72 L 130 82 L 125 86 L 135 92 L 125 98 L 130 104" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- ground rail -->
  <line x1="30" y1="100" x2="130" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
`);

const LED_CURRENT_LIMITER = frame(svg`
  <!-- V1 source -->
  <circle cx="40" cy="60" r="12" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="40" y1="20" x2="40" y2="48" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="40" y1="20" x2="120" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- R series -->
  <path d="M 120 20 L 120 36 L 114 44 L 126 52 L 114 60 L 126 68 L 120 76" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- LED triangle + cathode -->
  <polygon points="114,80 126,88 114,96" fill=${ACCENT} stroke=${ACCENT} stroke-width="1" />
  <line x1="126" y1="80" x2="126" y2="96" stroke=${ACCENT} stroke-width="1" />
  <line x1="130" y1="76" x2="134" y2="80" stroke=${ACCENT} stroke-width="0.8" />
  <line x1="132" y1="72" x2="136" y2="76" stroke=${ACCENT} stroke-width="0.8" />
  <line x1="120" y1="96" x2="120" y2="108" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="40" y1="72" x2="40" y2="108" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="40" y1="108" x2="120" y2="108" stroke=${STROKE} stroke-width="1" opacity="0.8" />
`);

const BRIDGE_RECTIFIER = frame(svg`
  <!-- AC source -->
  <circle cx="30" cy="60" r="14" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <path d="M 23 54 Q 27 48, 30 54 Q 33 60, 37 54" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- bridge -->
  <line x1="30" y1="46" x2="30" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="20" x2="80" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <polygon points="80,12 92,20 80,28" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="92" y1="12" x2="92" y2="28" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="92" y1="20" x2="120" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <polygon points="120,28 132,20 120,12" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="132" y1="12" x2="132" y2="28" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- output -->
  <line x1="132" y1="20" x2="132" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(132, 60)}
  <!-- ground -->
  <line x1="30" y1="74" x2="30" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="100" x2="132" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
`);

const DIODE_CLIPPER = frame(svg`
  <!-- input wire -->
  <line x1="20" y1="40" x2="40" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- R series -->
  <path d="M 40 40 L 48 36 L 56 44 L 64 36 L 72 44 L 80 36 L 88 44 L 96 40" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="96" y1="40" x2="120" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(120, 40)}
  <!-- D1 to top rail -->
  <line x1="120" y1="40" x2="120" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <polygon points="114,28 126,20 114,12" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="126" y1="12" x2="126" y2="28" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- D2 to ground rail -->
  <line x1="120" y1="40" x2="120" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <polygon points="114,60 126,68 114,76" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="126" y1="60" x2="126" y2="76" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="120" y1="76" x2="120" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${ground(120, 100)}
`);

const NMOS_SWITCH = frame(svg`
  <!-- MOSFET symbol — D top, S bottom, G left -->
  <line x1="80" y1="20" x2="80" y2="48" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="80" y1="48" x2="92" y2="48" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="80" y1="60" x2="92" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="80" y1="72" x2="92" y2="72" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="92" y1="40" x2="92" y2="80" stroke=${STROKE} stroke-width="1.4" opacity="0.8" />
  <line x1="80" y1="60" x2="60" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="92" y1="40" x2="92" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="92" y1="80" x2="92" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- Drain pin label -->
  <text x="98" y="24" font-family="JetBrains Mono" font-size="9" fill=${ACCENT}>D</text>
  <text x="98" y="106" font-family="JetBrains Mono" font-size="9" fill=${STROKE} opacity="0.6">S</text>
  <text x="42" y="64" font-family="JetBrains Mono" font-size="9" fill=${STROKE} opacity="0.6">G</text>
  <!-- top voltage label -->
  <line x1="60" y1="100" x2="92" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${ground(60, 100)}
  <!-- gate input pulse hint -->
  <path d="M 30 56 L 32 56 L 32 50 L 36 50 L 36 56 L 40 56 L 40 50 L 44 50 L 44 56" fill="none" stroke=${ACCENT} stroke-width="0.8" opacity="0.7" />
`);

const NPN_COMMON_EMITTER = frame(svg`
  <!-- BJT NPN symbol -->
  <circle cx="80" cy="60" r="18" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="72" y1="50" x2="72" y2="70" stroke=${STROKE} stroke-width="1.4" opacity="0.8" />
  <line x1="72" y1="55" x2="80" y2="48" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="72" y1="65" x2="80" y2="72" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <polygon points="76,68 80,70 76,72" fill=${STROKE} opacity="0.8" />
  <!-- Collector up -->
  <line x1="80" y1="48" x2="80" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- Emitter down to ground -->
  <line x1="80" y1="72" x2="80" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- Base input -->
  <line x1="50" y1="60" x2="72" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(80, 30, "Vc")}
  ${ground(80, 100)}
`);

const OPAMP_BUFFER = frame(svg`
  <!-- triangle -->
  <polygon points="60,40 60,84 100,62" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- + - markers -->
  <text x="64" y="54" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.8">−</text>
  <text x="64" y="78" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.8">+</text>
  <!-- input + -->
  <line x1="20" y1="74" x2="60" y2="74" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- output -->
  <line x1="100" y1="62" x2="135" y2="62" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(135, 62, "Vo")}
  <!-- feedback shorted to inv -->
  <line x1="100" y1="62" x2="100" y2="30" stroke=${STROKE} stroke-width="1" opacity="0.7" stroke-dasharray="2,2" />
  <line x1="100" y1="30" x2="50" y2="30" stroke=${STROKE} stroke-width="1" opacity="0.7" stroke-dasharray="2,2" />
  <line x1="50" y1="30" x2="50" y2="50" stroke=${STROKE} stroke-width="1" opacity="0.7" stroke-dasharray="2,2" />
  <line x1="50" y1="50" x2="60" y2="50" stroke=${STROKE} stroke-width="1" opacity="0.7" stroke-dasharray="2,2" />
`);

const OPAMP_INVERTING = frame(svg`
  <polygon points="70,40 70,84 104,62" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="76" y="53" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.8">−</text>
  <text x="76" y="78" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.8">+</text>
  <line x1="50" y1="74" x2="70" y2="74" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="50" y1="74" x2="50" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="104" y1="62" x2="130" y2="62" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- Rf feedback -->
  <path d="M 50 50 L 50 24 L 70 24 L 78 20 L 86 28 L 94 20 L 102 28 L 110 24 L 118 24 L 118 62 L 104 62" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- Rin -->
  <line x1="20" y1="50" x2="30" y2="50" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <path d="M 30 50 L 34 46 L 38 54 L 42 46 L 46 54 L 50 50" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(130, 62)}
`);

const OPAMP_NON_INVERTING = frame(svg`
  <polygon points="70,40 70,84 104,62" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="76" y="53" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.8">−</text>
  <text x="76" y="78" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.8">+</text>
  <!-- input on + -->
  <line x1="20" y1="74" x2="70" y2="74" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- output -->
  <line x1="104" y1="62" x2="130" y2="62" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- feedback to - input via Rf -->
  <line x1="118" y1="62" x2="118" y2="32" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <path d="M 118 32 L 110 28 L 102 36 L 94 28 L 86 36 L 78 28 L 70 32" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="70" y1="32" x2="50" y2="32" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="50" y1="32" x2="50" y2="50" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="50" y1="50" x2="70" y2="50" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- R to ground -->
  <line x1="50" y1="50" x2="50" y2="80" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="50" y1="80" x2="50" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${ground(50, 100)}
  ${nodeDot(130, 62)}
`);

const RC_HIGH_PASS = frame(svg`
  <!-- input wire -->
  <line x1="20" y1="40" x2="40" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- C series -->
  <line x1="40" y1="32" x2="40" y2="48" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="48" y1="32" x2="48" y2="48" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="48" y1="40" x2="100" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(100, 40)}
  <!-- R to ground -->
  <path d="M 100 40 L 100 50 L 96 54 L 104 60 L 96 66 L 104 72 L 100 76 L 100 90" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="100" y1="90" x2="100" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- input ground -->
  <line x1="20" y1="40" x2="20" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="20" y1="100" x2="100" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- bode hint -->
  <path d="M 122 90 Q 130 60, 142 50" fill="none" stroke=${ACCENT} stroke-width="0.8" opacity="0.7" />
`);

const RC_LOW_PASS = frame(svg`
  <line x1="20" y1="40" x2="40" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <path d="M 40 40 L 48 36 L 56 44 L 64 36 L 72 44 L 80 36 L 88 44 L 96 40" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="96" y1="40" x2="120" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(108, 40)}
  <line x1="108" y1="40" x2="108" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="100" y1="60" x2="116" y2="60" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="100" y1="68" x2="116" y2="68" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="108" y1="68" x2="108" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="20" y1="40" x2="20" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="20" y1="90" x2="108" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- bode -->
  <path d="M 120 50 Q 126 50, 132 70 Q 138 88, 144 90" fill="none" stroke=${ACCENT} stroke-width="0.8" opacity="0.7" />
`);

const RL_CIRCUIT = frame(svg`
  <line x1="20" y1="40" x2="40" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- R -->
  <path d="M 40 40 L 48 36 L 56 44 L 64 36 L 72 44 L 80 40" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="80" y1="40" x2="92" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- L coils -->
  <path d="M 92 40 Q 98 32, 104 40 Q 110 32, 116 40 Q 122 32, 128 40" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="128" y1="40" x2="140" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(140, 40)}
  <line x1="140" y1="40" x2="140" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="20" y1="40" x2="20" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="20" y1="100" x2="140" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- transient curve hint -->
  <path d="M 30 90 Q 50 86, 70 70 Q 90 56, 110 52" fill="none" stroke=${ACCENT} stroke-width="0.8" opacity="0.7" />
`);

const RLC_SERIES_RESONANCE = frame(svg`
  <line x1="20" y1="40" x2="40" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- R -->
  <path d="M 40 40 L 46 34 L 52 46 L 58 34 L 64 46 L 70 34 L 76 46 L 80 40" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="80" y1="40" x2="88" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- L -->
  <path d="M 88 40 Q 94 32, 100 40 Q 106 32, 112 40 Q 118 32, 124 40" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="124" y1="40" x2="140" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- C -->
  <line x1="140" y1="40" x2="140" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="132" y1="60" x2="148" y2="60" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="132" y1="68" x2="148" y2="68" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="140" y1="68" x2="140" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="20" y1="40" x2="20" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="20" y1="90" x2="140" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- resonance peak -->
  <path d="M 30 100 Q 40 100, 50 88 Q 60 60, 70 76 Q 80 90, 100 100" fill="none" stroke=${ACCENT} stroke-width="0.8" opacity="0.7" />
`);

const TRANSISTOR_SWITCH = frame(svg`
  <!-- BJT -->
  <circle cx="80" cy="60" r="16" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="74" y1="52" x2="74" y2="68" stroke=${STROKE} stroke-width="1.4" opacity="0.8" />
  <line x1="74" y1="56" x2="80" y2="50" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="74" y1="64" x2="80" y2="70" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <polygon points="76,67 80,69 76,71" fill=${STROKE} opacity="0.8" />
  <!-- Collector up -->
  <line x1="80" y1="50" x2="80" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="60" y1="20" x2="100" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="106" y="24" font-family="JetBrains Mono" font-size="9" fill=${ACCENT}>+V</text>
  <!-- Emitter ground -->
  <line x1="80" y1="70" x2="80" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${ground(80, 100)}
  <!-- Base input via R -->
  <line x1="20" y1="60" x2="38" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <path d="M 38 60 L 42 56 L 46 64 L 50 56 L 54 64 L 58 60" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="58" y1="60" x2="74" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="22" y="56" font-family="JetBrains Mono" font-size="9" fill=${STROKE} opacity="0.6">in</text>
`);

/**
 * Sprint 23 — opamp-astable preset (ADR-0048, 555-alternative).
 * Square-wave generator: comparator + RC timing + hysteresis
 * divider. Thumbnail Sprint 24 closure'da eklendi (preset
 * mevcut, asset eksikti).
 */
const OPAMP_ASTABLE = frame(svg`
  <!-- triangle -->
  <polygon points="68,40 68,84 104,62" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="74" y="53" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.8">−</text>
  <text x="74" y="78" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.8">+</text>
  <!-- output -->
  <line x1="104" y1="62" x2="135" y2="62" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(135, 62, "Vo")}
  <!-- RC timing on inv input -->
  <line x1="68" y1="50" x2="42" y2="50" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <path d="M 42 50 L 38 46 L 34 54 L 30 46 L 26 54 L 22 50" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="42" y1="50" x2="42" y2="80" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="34" y1="80" x2="50" y2="80" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="34" y1="86" x2="50" y2="86" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="42" y1="86" x2="42" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${ground(42, 100)}
  <!-- Feedback Rf out → inv timing pin -->
  <line x1="104" y1="62" x2="118" y2="62" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="118" y1="62" x2="118" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.7" stroke-dasharray="2,2" />
  <line x1="22" y1="20" x2="118" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.7" stroke-dasharray="2,2" />
  <line x1="22" y1="20" x2="22" y2="50" stroke=${STROKE} stroke-width="1" opacity="0.7" stroke-dasharray="2,2" />
  <!-- hysteresis divider on + input -->
  <line x1="68" y1="74" x2="56" y2="74" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="56" y1="74" x2="56" y2="62" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="56" y1="62" x2="56" y2="98" stroke=${STROKE} stroke-width="1" opacity="0.4" />
  <!-- square wave preview -->
  <path d="M 122 92 L 124 92 L 124 96 L 128 96 L 128 92 L 132 92 L 132 96 L 134 96" fill="none" stroke=${ACCENT} stroke-width="0.8" opacity="0.7" />
`);

const ZENER_REGULATOR = frame(svg`
  <!-- input + R series -->
  <line x1="20" y1="30" x2="36" y2="30" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="22" y="24" font-family="JetBrains Mono" font-size="9" fill=${STROKE} opacity="0.6">Vin</text>
  <path d="M 36 30 L 44 24 L 52 36 L 60 24 L 68 36 L 76 30" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="76" y1="30" x2="120" y2="30" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(120, 30, "Vo")}
  <!-- Zener pointing up -->
  <line x1="120" y1="30" x2="120" y2="50" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <polygon points="114,68 126,60 114,52" fill="none" stroke=${ACCENT} stroke-width="1" />
  <line x1="126" y1="52" x2="126" y2="68" stroke=${ACCENT} stroke-width="1" />
  <line x1="120" y1="68" x2="120" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${ground(120, 100)}
`);

// Sprint 50 preset additions (Sprint 52.1-hotfix — registry catch-up).
// 8 thumbnails authored to match Sprint 18 Paket B B5 visual language:
// 160×120 viewBox, --ivory primary stroke, --brass-hot accent for taps
// / signal hints, symbolic (not photoreal) topology.

const SIMPLE_RESISTOR_PAIR = frame(svg`
  <!-- V1 source -->
  <circle cx="30" cy="60" r="11" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="20" x2="30" y2="49" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="71" x2="30" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="20" x2="120" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="100" x2="120" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- R1 zigzag top -->
  <path d="M 120 20 L 120 26 L 114 30 L 126 36 L 114 42 L 126 48 L 120 54" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(120, 60, "mid")}
  <!-- R2 zigzag bottom -->
  <path d="M 120 66 L 120 72 L 114 78 L 126 84 L 114 90 L 126 96 L 120 100" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
`);

const PARALLEL_RESISTORS = frame(svg`
  <!-- V1 source -->
  <circle cx="30" cy="60" r="11" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="20" x2="30" y2="49" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="71" x2="30" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- top rail -->
  <line x1="30" y1="20" x2="130" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(80, 20)}
  <!-- bottom rail -->
  <line x1="30" y1="100" x2="130" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- R1 vertical zigzag -->
  <path d="M 90 20 L 90 28 L 84 32 L 96 38 L 84 44 L 96 50 L 84 56 L 96 62 L 84 68 L 96 74 L 84 80 L 90 84 L 90 100" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- R2 vertical zigzag -->
  <path d="M 130 20 L 130 28 L 124 32 L 136 38 L 124 44 L 136 50 L 124 56 L 136 62 L 124 68 L 136 74 L 124 80 L 130 84 L 130 100" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
`);

const THREE_RESISTOR_DIVIDER = frame(svg`
  <!-- V1 source -->
  <circle cx="30" cy="60" r="11" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="20" x2="30" y2="49" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="71" x2="30" y2="105" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="20" x2="120" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="105" x2="120" y2="105" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- R1 -->
  <path d="M 120 20 L 120 26 L 114 30 L 126 35 L 114 40 L 120 44" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(120, 49, "a")}
  <!-- R2 -->
  <path d="M 120 54 L 120 59 L 114 63 L 126 68 L 114 73 L 120 78" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(120, 82, "b")}
  <!-- R3 -->
  <path d="M 120 87 L 120 91 L 114 95 L 126 99 L 120 102 L 120 105" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
`);

const VOLTAGE_DOUBLER = frame(svg`
  <!-- AC source -->
  <circle cx="22" cy="60" r="12" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <path d="M 15 56 Q 19 50, 22 56 Q 25 62, 29 56" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="22" y1="48" x2="22" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="22" y1="72" x2="22" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="22" y1="40" x2="50" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- C1 horizontal -->
  <line x1="50" y1="32" x2="50" y2="48" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="58" y1="32" x2="58" y2="48" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="58" y1="40" x2="84" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(84, 40)}
  <!-- D1 to ground (anode up at gnd, cathode at mid → clamps mid negative on half cycle) -->
  <line x1="84" y1="40" x2="84" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <polygon points="78,76 90,76 84,60" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="78" y1="60" x2="90" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="84" y1="76" x2="84" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- D2 forward to v_out -->
  <line x1="84" y1="40" x2="100" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <polygon points="100,34 100,46 116,40" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="116" y1="34" x2="116" y2="46" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="116" y1="40" x2="138" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(138, 40, "2V")}
  <!-- C2 output filter -->
  <line x1="138" y1="40" x2="138" y2="58" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="130" y1="58" x2="146" y2="58" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="130" y1="66" x2="146" y2="66" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="138" y1="66" x2="138" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- ground rail -->
  <line x1="22" y1="100" x2="138" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
`);

const RC_BANDPASS = frame(svg`
  <line x1="14" y1="38" x2="30" y2="38" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- C1 series (high-pass) -->
  <line x1="30" y1="30" x2="30" y2="46" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="38" y1="30" x2="38" y2="46" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="38" y1="38" x2="68" y2="38" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- R1 shunt to ground -->
  <path d="M 68 38 L 68 46 L 64 50 L 72 56 L 64 62 L 72 68 L 68 72 L 68 90" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- mid wire continues -->
  <line x1="68" y1="38" x2="84" y2="38" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- R2 series (low-pass) -->
  <path d="M 84 38 L 92 34 L 100 42 L 108 34 L 116 42 L 120 38" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="120" y1="38" x2="146" y2="38" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(146, 38)}
  <!-- C2 shunt -->
  <line x1="132" y1="38" x2="132" y2="54" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="124" y1="54" x2="140" y2="54" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="124" y1="62" x2="140" y2="62" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="132" y1="62" x2="132" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- ground rail -->
  <line x1="14" y1="38" x2="14" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="14" y1="90" x2="146" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- bandpass bode hint (bell curve) -->
  <path d="M 22 110 Q 50 110, 70 102 Q 80 98, 90 102 Q 110 110, 138 110" fill="none" stroke=${ACCENT} stroke-width="0.8" opacity="0.7" />
`);

const RC_POWER_DECOUPLING = frame(svg`
  <!-- input rail vcc_raw -->
  <line x1="14" y1="38" x2="32" y2="38" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="14" y="32" font-family="JetBrains Mono" font-size="8" fill=${STROKE} opacity="0.6">Vcc</text>
  <!-- R_trace series -->
  <path d="M 32 38 L 38 34 L 44 42 L 50 34 L 56 42 L 62 38" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="62" y1="38" x2="148" y2="38" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(72, 38)}
  <!-- C_BULK (taller plates — electrolytic) -->
  <line x1="72" y1="38" x2="72" y2="54" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="64" y1="54" x2="80" y2="54" stroke=${STROKE} stroke-width="1.6" opacity="0.8" />
  <path d="M 66 62 Q 72 58, 78 62" fill="none" stroke=${STROKE} stroke-width="1.4" opacity="0.8" />
  <line x1="72" y1="64" x2="72" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="56" y="76" font-family="JetBrains Mono" font-size="7" fill=${ACCENT}>+</text>
  <!-- C_BYPASS (smaller — ceramic) -->
  <line x1="108" y1="38" x2="108" y2="54" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="100" y1="54" x2="116" y2="54" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="100" y1="62" x2="116" y2="62" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="108" y1="62" x2="108" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- R_LOAD -->
  <path d="M 144 38 L 144 46 L 140 50 L 148 56 L 140 62 L 148 68 L 144 72 L 144 90" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- ground rail -->
  <line x1="14" y1="38" x2="14" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="14" y1="90" x2="148" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- ripple suppression hint -->
  <path d="M 22 104 Q 30 100, 38 104 Q 46 108, 54 104 Q 62 100, 70 104 L 140 104" fill="none" stroke=${ACCENT} stroke-width="0.8" opacity="0.6" stroke-dasharray="2,2" />
`);

const RC_PHASE_SHIFT_OSC = frame(svg`
  <!-- op-amp inverting -->
  <polygon points="80,32 80,72 116,52" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="84" y="44" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.8">−</text>
  <text x="84" y="66" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.8">+</text>
  <!-- + input grounded -->
  <line x1="70" y1="64" x2="80" y2="64" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="70" y1="64" x2="70" y2="98" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- output -->
  <line x1="116" y1="52" x2="146" y2="52" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(146, 52)}
  <!-- output drop to ladder bus -->
  <line x1="140" y1="52" x2="140" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- 3-stage RC ladder (3× cap-resistor chain) -->
  <!-- C3 (rightmost) -->
  <line x1="140" y1="88" x2="128" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="124" y1="82" x2="124" y2="94" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="116" y1="82" x2="116" y2="94" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <!-- R3 shunt to gnd rail -->
  <line x1="116" y1="88" x2="116" y2="100" stroke=${STROKE} stroke-width="0.8" opacity="0.6" />
  <!-- C2 -->
  <line x1="116" y1="88" x2="100" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="96" y1="82" x2="96" y2="94" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="88" y1="82" x2="88" y2="94" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="88" y1="88" x2="88" y2="100" stroke=${STROKE} stroke-width="0.8" opacity="0.6" />
  <!-- C1 -->
  <line x1="88" y1="88" x2="72" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="68" y1="82" x2="68" y2="94" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="60" y1="82" x2="60" y2="94" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="60" y1="88" x2="60" y2="100" stroke=${STROKE} stroke-width="0.8" opacity="0.6" />
  <!-- ladder back to inverting input via R_FB feedback path -->
  <line x1="60" y1="88" x2="32" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="32" y1="88" x2="32" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.7" stroke-dasharray="2,2" />
  <line x1="32" y1="40" x2="80" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.7" stroke-dasharray="2,2" />
  <!-- ground rail -->
  <line x1="20" y1="100" x2="146" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- sine output hint -->
  <path d="M 122 24 Q 128 18, 134 24 Q 140 30, 146 24" fill="none" stroke=${ACCENT} stroke-width="0.8" opacity="0.7" />
`);

const ARDUINO_BLINK_LED = frame(svg`
  <!-- MCU box with PB5 pin -->
  <rect x="14" y="32" width="36" height="56" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="18" y="46" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.7">MCU</text>
  <text x="18" y="58" font-family="JetBrains Mono" font-size="7" fill=${ACCENT}>D13</text>
  <text x="18" y="70" font-family="JetBrains Mono" font-size="6" fill=${STROKE} opacity="0.6">PB5</text>
  <line x1="50" y1="44" x2="60" y2="44" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- R series 220 -->
  <path d="M 60 44 L 68 40 L 76 48 L 84 40 L 92 48 L 100 44" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- LED triangle -->
  <line x1="100" y1="44" x2="112" y2="44" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <polygon points="112,38 124,44 112,50" fill=${ACCENT} stroke=${ACCENT} stroke-width="1" />
  <line x1="124" y1="38" x2="124" y2="50" stroke=${ACCENT} stroke-width="1" />
  <line x1="128" y1="34" x2="132" y2="38" stroke=${ACCENT} stroke-width="0.8" />
  <line x1="130" y1="30" x2="134" y2="34" stroke=${ACCENT} stroke-width="0.8" />
  <line x1="124" y1="44" x2="140" y2="44" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="140" y1="44" x2="140" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${ground(140, 100)}
`);

const ARDUINO_BUTTON_INPUT = frame(svg`
  <!-- top rail Vcc -->
  <line x1="20" y1="20" x2="140" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="20" y="14" font-family="JetBrains Mono" font-size="8" fill=${STROKE} opacity="0.6">+5V</text>
  <!-- R_pullup -->
  <path d="M 80 20 L 80 28 L 74 32 L 86 38 L 74 44 L 86 50 L 80 54 L 80 60" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(80, 64, "in")}
  <!-- input pin to MCU box -->
  <line x1="80" y1="64" x2="116" y2="64" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <rect x="116" y="48" width="28" height="32" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="120" y="68" font-family="JetBrains Mono" font-size="7" fill=${ACCENT}>D2</text>
  <!-- switch SW1: input → ground (open) -->
  <line x1="80" y1="68" x2="80" y2="80" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <circle cx="80" cy="82" r="1.8" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="80" y1="84" x2="92" y2="92" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <circle cx="80" cy="96" r="1.8" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="80" y1="98" x2="80" y2="108" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- ground rail -->
  <line x1="20" y1="108" x2="140" y2="108" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${ground(40, 108)}
`);

const DIGITAL_AND_GATE = frame(svg`
  <!-- AND gate: rectangle + semicircle right side -->
  <path d="M 60 35 L 60 85 L 90 85 A 25 25 0 0 0 90 35 Z" fill="none" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <text x="68" y="64" font-family="JetBrains Mono" font-size="11" fill=${STROKE} opacity="0.7">&amp;</text>
  <!-- inputs -->
  <line x1="20" y1="48" x2="60" y2="48" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="20" y1="72" x2="60" y2="72" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="22" y="44" font-family="JetBrains Mono" font-size="7" fill=${ACCENT}>1</text>
  <text x="22" y="84" font-family="JetBrains Mono" font-size="7" fill=${ACCENT}>1</text>
  <!-- output -->
  <line x1="115" y1="60" x2="148" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(148, 60)}
  <text x="138" y="52" font-family="JetBrains Mono" font-size="8" fill=${ACCENT}>=1</text>
`);

const DIGITAL_NOT_GATE = frame(svg`
  <!-- NOT triangle -->
  <polygon points="60,38 60,82 108,60" fill="none" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <!-- inversion bubble -->
  <circle cx="114" cy="60" r="5" fill="none" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <!-- input -->
  <line x1="20" y1="60" x2="60" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="22" y="56" font-family="JetBrains Mono" font-size="7" fill=${ACCENT}>1</text>
  <!-- output -->
  <line x1="119" y1="60" x2="148" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(148, 60)}
  <text x="138" y="52" font-family="JetBrains Mono" font-size="8" fill=${ACCENT}>=0</text>
`);

const DIGITAL_XOR_GATE = frame(svg`
  <!-- XOR shape: OR curve + extra input curve -->
  <path d="M 50 35 Q 70 60, 50 85" fill="none" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <path d="M 58 35 Q 78 60, 58 85 L 80 85 Q 102 76, 110 60 Q 102 44, 80 35 Z" fill="none" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <text x="74" y="64" font-family="JetBrains Mono" font-size="10" fill=${STROKE} opacity="0.7">=1</text>
  <!-- inputs -->
  <line x1="20" y1="48" x2="56" y2="48" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="20" y1="72" x2="56" y2="72" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="22" y="44" font-family="JetBrains Mono" font-size="7" fill=${ACCENT}>1</text>
  <text x="22" y="84" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.6">0</text>
  <!-- output -->
  <line x1="110" y1="60" x2="148" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(148, 60)}
  <text x="138" y="52" font-family="JetBrains Mono" font-size="8" fill=${ACCENT}>=1</text>
`);

const HALF_WAVE_RECTIFIER = frame(svg`
  <!-- AC source -->
  <circle cx="30" cy="60" r="14" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <path d="M 22 56 Q 27 48, 30 56 Q 33 64, 38 56" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="20" x2="30" y2="46" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="74" x2="30" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="30" y1="20" x2="80" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- single diode forward -->
  <polygon points="80,14 80,26 96,20" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="96" y1="14" x2="96" y2="26" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="96" y1="20" x2="124" y2="20" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(124, 20, "Vo")}
  <!-- R_LOAD vertical -->
  <line x1="124" y1="20" x2="124" y2="36" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <path d="M 124 36 L 118 40 L 130 46 L 118 52 L 130 58 L 118 64 L 130 70 L 124 74" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="124" y1="74" x2="124" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- ground -->
  <line x1="30" y1="100" x2="124" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- half-wave hint (positive half only) -->
  <path d="M 36 110 Q 50 96, 64 110 L 80 110 Q 80 110, 80 110 L 110 110" fill="none" stroke=${ACCENT} stroke-width="0.8" opacity="0.7" />
`);

const WIEN_BRIDGE_OSC = frame(svg`
  <!-- op-amp non-inverting -->
  <polygon points="64,40 64,80 100,60" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="68" y="52" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.8">+</text>
  <text x="68" y="74" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.8">−</text>
  <!-- output -->
  <line x1="100" y1="60" x2="146" y2="60" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(146, 60)}
  <!-- Wien series (out → v_p): R + C -->
  <line x1="130" y1="60" x2="130" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <path d="M 130 90 L 124 86 L 116 94 L 108 86 L 100 94 L 96 90" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="96" y1="90" x2="86" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="82" y1="86" x2="82" y2="94" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <line x1="74" y1="86" x2="74" y2="94" stroke=${STROKE} stroke-width="1.5" opacity="0.8" />
  <!-- v_p node up to + input -->
  <line x1="74" y1="90" x2="58" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="58" y1="90" x2="58" y2="48" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="58" y1="48" x2="64" y2="48" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(58, 90)}
  <!-- Wien parallel (v_p → gnd): R || C -->
  <path d="M 50 90 L 46 86 L 38 94 L 30 86 L 22 94 L 14 90" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="50" y1="90" x2="58" y2="90" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="14" y1="90" x2="14" y2="104" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- feedback divider (Rf above, Rg below) on – input -->
  <line x1="100" y1="60" x2="100" y2="28" stroke=${STROKE} stroke-width="1" opacity="0.7" stroke-dasharray="2,2" />
  <line x1="100" y1="28" x2="44" y2="28" stroke=${STROKE} stroke-width="1" opacity="0.7" stroke-dasharray="2,2" />
  <line x1="44" y1="28" x2="44" y2="72" stroke=${STROKE} stroke-width="1" opacity="0.7" stroke-dasharray="2,2" />
  <line x1="44" y1="72" x2="64" y2="72" stroke=${STROKE} stroke-width="1" opacity="0.7" stroke-dasharray="2,2" />
  <!-- ground -->
  <line x1="14" y1="104" x2="146" y2="104" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- sine hint -->
  <path d="M 122 18 Q 128 12, 134 18 Q 140 24, 146 18" fill="none" stroke=${ACCENT} stroke-width="0.8" opacity="0.7" />
`);

// Sprint 54.3-2 — Father's circuit canonical regression devresi.
// VISION §10. Sprint 18 B5 visual language: symbolic topology, not
// photorealistic. Top-level structure: AC mains → sense divider →
// LM393 comparator → BC547 + MOC → TRIAC → cap bank.
const FATHERS_CAP_BANK_CONTROLLER = frame(svg`
  <!-- AC mains source (top-left) -->
  <circle cx="20" cy="20" r="10" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <path d="M 13 18 Q 17 14, 20 18 Q 23 22, 27 18" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="34" y="22" font-family="JetBrains Mono" font-size="7" fill=${STROKE} opacity="0.6">AC</text>
  <!-- Sense divider rail -->
  <line x1="20" y1="30" x2="20" y2="58" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <path d="M 20 36 L 24 38 L 16 42 L 24 46 L 16 50 L 20 52" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- Bridge rectifier (small diamond) -->
  <polygon points="40,40 50,32 60,40 50,48" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="20" y1="58" x2="40" y2="58" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="40" y1="58" x2="40" y2="40" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- LM393 comparator triangle -->
  <polygon points="70,30 70,55 90,42" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="74" y="38" font-family="JetBrains Mono" font-size="6" fill=${STROKE} opacity="0.8">+</text>
  <text x="74" y="52" font-family="JetBrains Mono" font-size="6" fill=${STROKE} opacity="0.8">−</text>
  <line x1="60" y1="40" x2="70" y2="36" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="50" y1="60" x2="70" y2="50" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  ${nodeDot(90, 42, "U1")}
  <!-- BC547 transistor (circle + lines) -->
  <line x1="90" y1="42" x2="100" y2="42" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <circle cx="105" cy="42" r="6" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="101" y1="38" x2="101" y2="46" stroke=${STROKE} stroke-width="1.4" opacity="0.8" />
  <line x1="101" y1="42" x2="99" y2="42" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="101" y1="39" x2="105" y2="36" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="101" y1="45" x2="105" y2="48" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <text x="106" y="60" font-family="JetBrains Mono" font-size="6" fill=${STROKE} opacity="0.6">Q1</text>
  <!-- MOC3041 opto box (galvanic isolation) -->
  <rect x="115" y="35" width="20" height="20" fill="none" stroke=${ACCENT} stroke-width="1" opacity="0.9" stroke-dasharray="2,1" />
  <line x1="119" y1="40" x2="123" y2="40" stroke=${ACCENT} stroke-width="0.6" />
  <polygon points="119,46 123,42 119,42" fill=${ACCENT} stroke=${ACCENT} stroke-width="0.6" />
  <line x1="127" y1="40" x2="127" y2="50" stroke=${ACCENT} stroke-width="0.6" />
  <line x1="131" y1="40" x2="131" y2="50" stroke=${ACCENT} stroke-width="0.6" />
  <text x="116" y="65" font-family="JetBrains Mono" font-size="6" fill=${ACCENT} opacity="0.8">MOC</text>
  <!-- BT136 TRIAC symbol (TRIAC distinctive shape) -->
  <line x1="135" y1="42" x2="145" y2="42" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <polygon points="145,36 152,42 145,48" fill="none" stroke=${ACCENT} stroke-width="1.2" />
  <polygon points="152,36 145,42 152,48" fill="none" stroke=${ACCENT} stroke-width="1.2" />
  <line x1="148" y1="35" x2="148" y2="42" stroke=${ACCENT} stroke-width="0.8" />
  <text x="142" y="62" font-family="JetBrains Mono" font-size="6" fill=${ACCENT} opacity="0.9">Q2</text>
  <!-- Capacitor bank (10× small caps) -->
  <line x1="148" y1="42" x2="148" y2="78" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="14" y1="78" x2="148" y2="78" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="14" y1="78" x2="14" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="148" y1="78" x2="148" y2="100" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- 10 mini caps in row -->
  <line x1="22" y1="82" x2="22" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="18" y1="88" x2="26" y2="88" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="18" y1="92" x2="26" y2="92" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="22" y1="92" x2="22" y2="98" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="36" y1="82" x2="36" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="32" y1="88" x2="40" y2="88" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="32" y1="92" x2="40" y2="92" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="36" y1="92" x2="36" y2="98" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="50" y1="82" x2="50" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="46" y1="88" x2="54" y2="88" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="46" y1="92" x2="54" y2="92" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="50" y1="92" x2="50" y2="98" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="64" y1="82" x2="64" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="60" y1="88" x2="68" y2="88" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="60" y1="92" x2="68" y2="92" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="64" y1="92" x2="64" y2="98" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="78" y1="82" x2="78" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="74" y1="88" x2="82" y2="88" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="74" y1="92" x2="82" y2="92" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="78" y1="92" x2="78" y2="98" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="92" y1="82" x2="92" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="88" y1="88" x2="96" y2="88" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="88" y1="92" x2="96" y2="92" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="92" y1="92" x2="92" y2="98" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="106" y1="82" x2="106" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="102" y1="88" x2="110" y2="88" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="102" y1="92" x2="110" y2="92" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="106" y1="92" x2="106" y2="98" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="120" y1="82" x2="120" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="116" y1="88" x2="124" y2="88" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="116" y1="92" x2="124" y2="92" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="120" y1="92" x2="120" y2="98" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="134" y1="82" x2="134" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="130" y1="88" x2="138" y2="88" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="130" y1="92" x2="138" y2="92" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="134" y1="92" x2="134" y2="98" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="148" y1="82" x2="148" y2="88" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <line x1="144" y1="88" x2="152" y2="88" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="144" y1="92" x2="152" y2="92" stroke=${STROKE} stroke-width="1.2" opacity="0.8" />
  <line x1="148" y1="92" x2="148" y2="98" stroke=${STROKE} stroke-width="1" opacity="0.8" />
  <!-- "BABA" small label -->
  <text x="62" y="115" font-family="EB Garamond" font-style="italic" font-size="10" fill=${ACCENT} opacity="0.9">babanın devresi</text>
`);

/**
 * Registry of preset.id → thumbnail SVG template. Keeps lookup
 * O(1) at render time. Missing entries fall through to a generic
 * "circuit" placeholder so the layout never breaks.
 */
const REGISTRY: Readonly<Record<string, TemplateResult>> = {
  // basic — series / parallel resistor primitives
  // Sprint 54.3-2 — VISION §10 canonical regression devresi (babanın
  // 50+ component cap bank controller proxy). Listenin başında özel
  // statüsünü vurgulamak için.
  "fathers-cap-bank-controller": FATHERS_CAP_BANK_CONTROLLER,
  "voltage-divider": VOLTAGE_DIVIDER,
  "simple-resistor-pair": SIMPLE_RESISTOR_PAIR,
  "parallel-resistors": PARALLEL_RESISTORS,
  "three-resistor-divider": THREE_RESISTOR_DIVIDER,
  // semiconductor + power
  "led-current-limiter": LED_CURRENT_LIMITER,
  "half-wave-rectifier": HALF_WAVE_RECTIFIER,
  "bridge-rectifier": BRIDGE_RECTIFIER,
  "voltage-doubler": VOLTAGE_DOUBLER,
  "diode-clipper": DIODE_CLIPPER,
  "nmos-switch": NMOS_SWITCH,
  "npn-common-emitter": NPN_COMMON_EMITTER,
  "transistor-switch": TRANSISTOR_SWITCH,
  "zener-regulator": ZENER_REGULATOR,
  // MCU topology
  "arduino-blink-led": ARDUINO_BLINK_LED,
  "arduino-button-input": ARDUINO_BUTTON_INPUT,
  // digital logic
  "digital-and-gate": DIGITAL_AND_GATE,
  "digital-not-gate": DIGITAL_NOT_GATE,
  "digital-xor-gate": DIGITAL_XOR_GATE,
  // op-amp + oscillator
  "opamp-buffer": OPAMP_BUFFER,
  "opamp-inverting": OPAMP_INVERTING,
  "opamp-non-inverting": OPAMP_NON_INVERTING,
  "opamp-astable": OPAMP_ASTABLE,
  "rc-phase-shift-osc": RC_PHASE_SHIFT_OSC,
  "wien-bridge-osc": WIEN_BRIDGE_OSC,
  // RC filters + power decoupling
  "rc-high-pass": RC_HIGH_PASS,
  "rc-low-pass": RC_LOW_PASS,
  "rc-bandpass": RC_BANDPASS,
  "rc-power-decoupling": RC_POWER_DECOUPLING,
  // RL / RLC passives
  "rl-circuit": RL_CIRCUIT,
  "rlc-series-resonance": RLC_SERIES_RESONANCE,
};

const FALLBACK = frame(svg`
  <rect x="20" y="30" width="120" height="60" fill="none" stroke=${STROKE} stroke-width="1" opacity="0.5" stroke-dasharray="3,3" />
  <text x="80" y="64" text-anchor="middle" font-family="JetBrains Mono" font-size="10" fill=${STROKE} opacity="0.6" letter-spacing="0.2em">CIRCUIT</text>
`);

export function thumbnailFor(presetId: string): TemplateResult {
  return REGISTRY[presetId] ?? FALLBACK;
}

/** Test helper — exposes the registry size + ids for coverage. */
export const PRESET_THUMBNAIL_IDS: readonly string[] = Object.keys(REGISTRY);
